import React, { useState } from "react";
import Layout from "../../components/Layout/Layout";
import "./Contact.css";

const Contact: React.FC = () => {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
        rgpd: false
    });

    const [errors, setErrors] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
        rgpd: ""
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const validateEmail = (email: string) => {
        const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return re.test(String(email).toLowerCase());
    };

    const validatePhone = (phone: string) => {
        const re = /^(\+?\d{1,3}[- ]?)?\(?\d{2,3}\)?[- ]?\d{2,3}[- ]?\d{4}$/;
        return re.test(phone);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        if (errors[name as keyof typeof errors]) {
            setErrors({
                ...errors,
                [name]: ""
            });
        }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData({
            ...formData,
            [name]: checked
        });

        if (errors[name as keyof typeof errors]) {
            setErrors({
                ...errors,
                [name]: ""
            });
        }
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            message: "",
            rgpd: ""
        };

        if (!formData.firstName.trim()) {
            newErrors.firstName = "Le prénom est obligatoire";
            valid = false;
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = "Le nom est obligatoire";
            valid = false;
        }

        if (!formData.email.trim()) {
            newErrors.email = "L'email est obligatoire";
            valid = false;
        } else if (!validateEmail(formData.email)) {
            newErrors.email = "Veuillez entrer un email valide";
            valid = false;
        }

        if (!formData.phone.trim()) {
            newErrors.phone = "Le téléphone est obligatoire";
            valid = false;
        } else if (!validatePhone(formData.phone)) {
            newErrors.phone = "Veuillez entrer un numéro de téléphone valide";
            valid = false;
        }

        if (!formData.message.trim()) {
            newErrors.message = "Le message est obligatoire";
            valid = false;
        }

        if (!formData.rgpd) {
            newErrors.rgpd = "Vous devez accepter les conditions d'utilisation";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const response = await fetch('/api/send_mail', { // Relative path works if CF is configured
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phone: formData.phone,
                    message: formData.message
                }),
            });

            if (response.ok) {
                setSubmitSuccess(true);
                setFormData({
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    message: "",
                    rgpd: false
                });
            } else {
                throw new Error('Erreur lors de l\'envoi du message');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer plus tard.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitSuccess) {
        return (
            <Layout>
                <section className="contact">
                    <div className="contact-intro">
                        <h2 className="success-title">Message envoyé avec succès!</h2>
                        <p>Nous vous répondrons dans les plus brefs délais.</p>
                    </div>
                </section>
            </Layout>
        );
    }

    return (
        <Layout>
            <section className="contact">
                <h2 className="section-title">Contactez-nous</h2>

                <div className="contact-intro">
                    <p>
                        Vous êtes intéressés et vous souhaitez en savoir davantage ? Nous nous tenons à votre
                        disposition afin de répondre à toutes vos questions et vous accompagner dans la
                        concrétisation de tous vos projets immobiliers.
                    </p>
                </div>

                <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <div className="name-fields">
                            <div className="input-group">
                                <label htmlFor="firstName" className="form-label">
                                    Prénom <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className={`form-input ${errors.firstName ? 'input-error' : ''}`}
                                    placeholder="Votre prénom"
                                    disabled={isSubmitting}
                                />
                                {errors.firstName && <span className="error">{errors.firstName}</span>}
                            </div>

                            <div className="input-group">
                                <label htmlFor="lastName" className="form-label">
                                    Nom <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className={`form-input ${errors.lastName ? 'input-error' : ''}`}
                                    placeholder="Votre nom"
                                    disabled={isSubmitting}
                                />
                                {errors.lastName && <span className="error">{errors.lastName}</span>}
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <div className="input-group">
                            <label htmlFor="email" className="form-label">
                                Email <span className="required">*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`form-input ${errors.email ? 'input-error' : ''}`}
                                placeholder="votre@email.com"
                                disabled={isSubmitting}
                            />
                            {errors.email && <span className="error">{errors.email}</span>}
                        </div>

                        <div className="input-group">
                            <label htmlFor="phone" className="form-label">
                                Téléphone <span className="required">*</span>
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className={`form-input ${errors.phone ? 'input-error' : ''}`}
                                placeholder="01 23 45 67 89"
                                disabled={isSubmitting}
                            />
                            {errors.phone && <span className="error">{errors.phone}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <div className="input-group">
                            <label htmlFor="message" className="form-label">
                                Message <span className="required">*</span>
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                className={`form-textarea ${errors.message ? 'input-error' : ''}`}
                                placeholder="Votre message..."
                                rows={6}
                                disabled={isSubmitting}
                            />
                            {errors.message && <span className="error">{errors.message}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <div className="checkbox-group">
                            <input
                                type="checkbox"
                                id="rgpd"
                                name="rgpd"
                                checked={formData.rgpd}
                                onChange={handleCheckboxChange}
                                className="form-checkbox"
                                disabled={isSubmitting}
                            />
                            <label htmlFor="rgpd" className="checkbox-label">
                                J'accepte les <a href="/mentions-legales" className="conditions-link" target="_blank" rel="noopener noreferrer">conditions d'utilisation des données</a> <span className="required">*</span>
                            </label>
                        </div>
                        {errors.rgpd && <span className="error">{errors.rgpd}</span>}
                    </div>

                    <div className="form-actions">
                        <button
                            type="submit"
                            className="submit-button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                        </button>
                    </div>
                </form>
            </section>
        </Layout>
    );
};

export default Contact;
